import { useLingui } from "@lingui/react/macro";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@rakazo/ui-web";
import {
  Bell,
  ChevronLeft,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
} from "lucide-react";
import { useState } from "react";
import { IconTooltip } from "../../components/IconTooltip";
import { desktopBridge } from "../../lib/desktop";
import type { Panel } from "../../lib/right-panel-state";
import { WindowChrome } from "../WindowChrome";
import { BotCreatePicker } from "./bot-picker";

export function SidebarHeader({
  mini = false,
  inline = false,
  onCompactAction,
  onSearch,
  activityMode,
  toggleActivityMode,
  setBotsSidebarCollapsedPref,
  setMobileSidebarOpen,
  setPanel,
  setNewSpaceOpen,
}: {
  mini?: boolean;
  inline?: boolean;
  onCompactAction?: () => void;
  onSearch?: () => void;
  activityMode: boolean;
  toggleActivityMode: () => void;
  setBotsSidebarCollapsedPref: (collapsed: boolean) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  setPanel: (panel: Panel) => void;
  setNewSpaceOpen: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [miniCreateOpen, setMiniCreateOpen] = useState(false);
  const closeCreateMenu = () => {
    setCreateMenuOpen(false);
    setMiniCreateOpen(false);
    onCompactAction?.();
  };
  const hasWindowChrome = Boolean(desktopBridge());
  const createPicker = (
    <BotCreatePicker
      onCreateBot={() => {
        closeCreateMenu();
        setMobileSidebarOpen(false);
        setPanel("create");
      }}
      onCreateGroup={() => {
        closeCreateMenu();
        setMobileSidebarOpen(false);
        setPanel("create-group");
      }}
      onCreateSpace={() => {
        closeCreateMenu();
        setMobileSidebarOpen(false);
        setNewSpaceOpen(true);
      }}
    />
  );
  const toggleLabel = mini ? t`Show bots` : t`Minimize bots`;
  if (mini) {
    const content = (
      <div className="min-w-0" data-testid="sidebar-compact-actions">
        {miniCreateOpen ? (
          <>
            <Button
              variant="ghost"
              className="min-h-11 w-full justify-start gap-2"
              onClick={() => setMiniCreateOpen(false)}
            >
              <ChevronLeft size={16} aria-hidden="true" />
              {t`Back`}
            </Button>
            {createPicker}
          </>
        ) : (
          <>
            {onSearch ? (
              <Button
                variant="ghost"
                className="min-h-11 w-full justify-start gap-2.5"
                onClick={() => {
                  closeCreateMenu();
                  onSearch();
                }}
              >
                <Search size={16} aria-hidden="true" />
                {t`Search`}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              className="min-h-11 w-full justify-start gap-2.5"
              aria-pressed={activityMode}
              onClick={() => {
                closeCreateMenu();
                toggleActivityMode();
              }}
            >
              <Bell size={16} aria-hidden="true" />
              {t`Activity`}
            </Button>
            <Button
              variant="ghost"
              className="min-h-11 w-full justify-start gap-2.5"
              data-testid="create-menu-trigger"
              onClick={() => setMiniCreateOpen(true)}
            >
              <Plus size={16} aria-hidden="true" />
              {t`Create`}
            </Button>
          </>
        )}
      </div>
    );
    if (inline) return content;
    return (
      <div className="app-no-drag flex shrink-0 justify-center py-1">
        <Popover
          open={createMenuOpen}
          onOpenChange={(open) => {
            setCreateMenuOpen(open);
            if (!open) setMiniCreateOpen(false);
          }}
        >
          <IconTooltip label={t`More`}>
            <PopoverTrigger
              data-testid="sidebar-more-trigger"
              aria-label={t`More`}
              className="app-no-drag grid size-11 place-items-center rounded-full text-muted-foreground outline-none hover:bg-sidebar-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              <MoreHorizontal size={20} strokeWidth={1.8} aria-hidden="true" />
            </PopoverTrigger>
          </IconTooltip>
          {createMenuOpen ? (
            <PopoverContent
              side={document.documentElement.dir === "rtl" ? "left" : "right"}
              align="end"
              className={`app-no-drag gap-0 overflow-hidden data-closed:animate-none ${miniCreateOpen ? "w-auto p-0" : "w-64 p-1"}`}
            >
              {content}
            </PopoverContent>
          ) : null}
        </Popover>
      </div>
    );
  }
  return (
    <div className="app-drag flex items-center justify-between px-3 pb-3 pt-4">
      <WindowChrome />
      {!hasWindowChrome ? (
        <span className="text-sm font-semibold tracking-tight text-foreground">HrokBot</span>
      ) : null}
      <div className="relative flex items-center gap-1">
        <IconTooltip label={t`Activity`}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t`Activity`}
            aria-pressed={activityMode}
            data-activity-mode={activityMode ? "on" : "off"}
            onClick={toggleActivityMode}
            className={`app-no-drag flex h-11 w-11 items-center justify-center rounded-full ${
              activityMode
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground/70 hover:text-foreground/75"
            }`}
          >
            <Bell
              size={15}
              strokeWidth={1.8}
              fill={activityMode ? "currentColor" : "none"}
              aria-hidden="true"
            />
          </Button>
        </IconTooltip>
        <IconTooltip label={toggleLabel}>
          <Button
            variant="ghost"
            size="icon-sm"
            className="app-no-drag hidden h-11 w-11 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground/75 md:inline-flex"
            aria-label={toggleLabel}
            data-testid="minimize-bots-sidebar"
            onClick={() => setBotsSidebarCollapsedPref(true)}
          >
            <PanelLeftClose size={15} strokeWidth={1.8} aria-hidden="true" />
          </Button>
        </IconTooltip>
        <Popover open={createMenuOpen} onOpenChange={setCreateMenuOpen}>
          <IconTooltip label={t`Create`}>
            <PopoverTrigger
              className="app-no-drag flex size-11 items-center justify-center rounded-full text-muted-foreground/70 outline-none hover:text-foreground/75 focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t`Create`}
              data-testid="create-menu-trigger"
            >
              <Plus size={16} strokeWidth={1.8} aria-hidden="true" />
            </PopoverTrigger>
          </IconTooltip>
          {/* Unmount with the state change so the panel it opens never coexists with the menu. */}
          {createMenuOpen ? (
            <PopoverContent
              align="end"
              className="app-no-drag w-auto gap-0 overflow-hidden p-0 data-closed:animate-none"
            >
              {createPicker}
            </PopoverContent>
          ) : null}
        </Popover>
      </div>
    </div>
  );
}

export function CompactSidebarTop({ onExpand }: { onExpand: () => void }) {
  const { t } = useLingui();
  return (
    <div className="shrink-0" data-testid="compact-sidebar-top">
      {desktopBridge() ? (
        <div
          className="app-drag flex h-12 items-center justify-center"
          data-testid="compact-window-chrome"
        >
          <WindowChrome />
        </div>
      ) : null}
      <div className="flex justify-center py-1">
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`Show bots`}
          data-testid="restore-bots-sidebar"
          className="app-no-drag size-11 rounded-full text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
          onClick={onExpand}
        >
          <PanelLeftOpen size={18} strokeWidth={1.8} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
